import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { storeLogin } from "../../Redux/userSlice";
import FormLayout from "../../components/FormLayout/FormLayout";
import Checkbox from "../../components/Atoms/Checkbox/Checkbox";
import EmailInput from "../../components/Atoms/EmailInput";
import PasswordInput from "../../components/Atoms/password/PasswordInput";
import Loader from "../../components/Loader/Loader";
import { CiUser, CiLock } from "react-icons/ci";
import FormSubmitButton from "../../components/Atoms/FormButton/FormSubmitButton";
import { toast } from "sonner";
import { removeCookie, setCookie } from "../../context/useCookies";

const StoreLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { authSlice } = useSelector((state) => state);
  const { loading } = authSlice || {};
  const [loginError, setLoginError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [formState, setFormState] = useState("StoreLogin");
  const [verificationCode, setVerificationCode] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const [formFields, setFormFields] = useState({
    email: "",
    password: "",
    forgotEmail: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [formErrors, setFormErrors] = useState({
    email: null,
    password: null,
    forgotEmail: null,
    verificationCode: null,
    newPassword: null,
    confirmNewPassword: null,
  });
  const [formAnimation, setFormAnimation] = useState("slide-in");

  useEffect(() => {
    setFormAnimation("slide-out");
    const timer = setTimeout(() => {
      setFormAnimation("slide-in");
    }, 300);

    return () => clearTimeout(timer);
  }, [formState]);

  const validateLoginFields = useCallback(() => {
    let isValid = true;
    const errors = {};
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;

    if (formState === "login") {
      if (!formFields.email.trim()) {
        errors.email = "User Name is required.";
        isValid = false;
      }

      if (!formFields.password.trim()) {
        errors.password = "Password is required.";
        isValid = false;
      } else if (formFields.password.length < 6) {
        errors.password = "Password must be at least 6 characters long.";
        isValid = false;
      }
    } else if (formState === "forgotPassword") {
      if (!formFields.forgotEmail.trim()) {
        errors.forgotEmail = "Email is required.";
        isValid = false;
      } else if (!emailRegex.test(formFields.forgotEmail)) {
        errors.forgotEmail = "Please enter a valid email address.";
        isValid = false;
      }
    } else if (formState === "verificationCode") {
      const code = verificationCode.join("");
      if (code.length !== 6) {
        errors.verificationCode =
          "Please enter the complete verification code.";
        isValid = false;
      }
    } else if (formState === "resetPassword") {
      if (!formFields.newPassword.trim()) {
        errors.newPassword = "New password is required.";
        isValid = false;
      } else if (!passwordRegex.test(formFields.newPassword)) {
        errors.newPassword =
          "Password must be at least 8 characters long, include at least one uppercase letter, one lowercase letter, one number, and one special character (e.g., @$!%*?&).";
        isValid = false;
      }

      if (!formFields.confirmNewPassword.trim()) {
        errors.confirmNewPassword = "Please confirm your new password.";
        isValid = false;
      } else if (
        formFields.newPassword.trim() &&
        formFields.confirmNewPassword.trim() &&
        formFields.newPassword !== formFields.confirmNewPassword
      ) {
        errors.confirmNewPassword = "Passwords do not match.";
        isValid = false;
      }
    }

    setFormErrors(errors);
    return isValid;
  }, [formState, formFields, verificationCode]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormFields((prev) => ({ ...prev, [name]: value.trim() }));
    setFormErrors((prev) => ({ ...prev, [name]: null }));
    setLoginError("");
  }, []);

  const resetForm = useCallback(() => {
    setFormFields({
      email: "",
      password: "",
      forgotEmail: "",
      newPassword: "",
      confirmNewPassword: "",
    });
    setVerificationCode(["", "", "", "", "", ""]);
    setRememberMe(false);
    setFormErrors({
      email: null,
      password: null,
      forgotEmail: null,
      verificationCode: null,
      newPassword: null,
      confirmNewPassword: null,
    });
  }, []);

  const storeOrClearCredentials = useCallback(
    (email, password) => {
      if (rememberMe && email && password) {
        setCookie("eyisionAdmin", JSON.stringify({ email, password }));
      } else {
        removeCookie("eyisionAdmin");
      }
    },
    [rememberMe]
  );

  const handleApiResponse = useCallback(
    (res, currentFormState) => {
      if (res?.error) {
        toast.error(res.payload || res?.error?.message);
        setFormAnimation("error");
        setTimeout(() => setFormAnimation("slide-in"), 500);
        return false;
      }
      setFormAnimation("success-animation");
      toast.success(res.payload?.message);
      setTimeout(() => {
        if (currentFormState === "login") {
          setCookie("eyisionAdmin", JSON.stringify(res.payload?.data));
          resetForm();
          navigate("/app/home");
        } else if (currentFormState === "forgotPassword") {
          setCookie(
            "eyisionAdminForgotToken",
            JSON.stringify(res.payload?.data?.token)
          );
          setFormState("verificationCode");
        } else if (currentFormState === "verificationCode") {
          setFormState("resetPassword");
        } else if (currentFormState === "resetPassword") {
          localStorage.removeItem("eyisionAdminForgotToken");
          removeCookie("eyisionAdminForgotToken");
          resetForm();
          setFormState("login");
        }
      }, 600);

      return true;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [navigate, resetForm]
  );

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!validateLoginFields()) {
      setFormAnimation("shake");
      setTimeout(() => setFormAnimation("slide-in"), 500);
      return;
    }

    const { email, password } = formFields;
    storeOrClearCredentials(email, password);

    try {
      setFormAnimation("loading");
      const response = await dispatch(
        storeLogin({ userName: email, password })
      );
      handleApiResponse(response, "login");
    } catch (error) {
      toast.error("Login failed. Please try again.");
    }
  };

  const getAnimationClasses = useCallback(() => {
    switch (formAnimation) {
      case "slide-in":
        return "animate-slide-in opacity-100 transform translate-x-0";
      case "slide-out":
        return "animate-slide-out opacity-0 transform -translate-x-full";
      case "shake":
        return "animate-shake";
      case "loading":
        return "animate-pulse";
      case "success-animation":
        return "animate-success-bounce";
      case "error":
        return "animate-error";
      default:
        return "";
    }
  }, [formAnimation]);

  const FloatingElement = ({ delay, size, position }) => (
    <div
      className={`absolute rounded-full bg-gradient-to-r from-blue-200 to-blue-400 opacity-20 animate-float ${size} ${position}`}
      style={{ animationDelay: delay }}
    ></div>
  );

  const renderForm = () => {
    const animationClasses = getAnimationClasses();

    switch (formState) {
      case "StoreLogin":
        return (
          <FormLayout
            title="Store Sign in"
            subTitle="Please enter your login credentials"
            onSubmit={handleLoginSubmit}
            bottomText="Don't have an account?"
            linkText="Sign up"
            linkTo="/signup"
            className={`${animationClasses} transition-all duration-300`}
            showGoBack={true}
          >
            <div className="relative z-10 flex flex-col gap-3">
              <div className="transition-all duration-300 ease-in-out transform hover:scale-[1.01] hover:shadow-md rounded-lg">
                <EmailInput
                  id="email"
                  name="email"
                  label="User Name"
                  value={formFields.email}
                  placeholder="Enter your username"
                  icon={CiUser}
                  onChange={handleInputChange}
                  errorMessage={formErrors.email}
                  autoFocus
                  className="animate-fade-in"
                />
              </div>
              <div
                className="transition-all duration-300 ease-in-out transform hover:scale-[1.01] hover:shadow-md rounded-lg mt-2 animate-fade-in"
                style={{ animationDelay: "0.2s" }}
              >
                <PasswordInput
                  id="password"
                  name="password"
                  label="Password"
                  value={formFields.password}
                  placeholder="***********"
                  icon={CiLock}
                  onChange={handleInputChange}
                  errorMessage={formErrors.password}
                />
              </div>

              {loginError && (
                <div className="p-2 text-sm text-red-800 rounded-md animate-fade-in bg-red-50">
                  {loginError}
                </div>
              )}

              <div
                className="flex justify-start mt-2 mb-2 animate-fade-in"
                style={{ animationDelay: "0.3s" }}
              >
                <label className="flex items-center text-sm cursor-pointer gap-x-2">
                  <Checkbox
                    id="remember_me"
                    name="remember_me"
                    className="w-4 h-4 transition-transform rounded hover:scale-110"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span className="text-[#000000AB]">Remember Me</span>
                </label>
              </div>

              <div
                className="animate-fade-in"
                style={{ animationDelay: "0.4s" }}
              >
                <FormSubmitButton
                  buttonLabel={loading ? "Logging in..." : "Login"}
                  className="transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-95"
                />
              </div>
            </div>
          </FormLayout>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    const rememberData = JSON.parse(
      localStorage.getItem("eyisionAdmin") || null
    );

    if (rememberData && rememberData?.email && rememberData?.password) {
      setRememberMe(true);
      setFormFields((prev) => ({
        ...prev,
        email: rememberData?.email,
        password: rememberData?.password,
      }));
    }
  }, []);

  return (
    <div className="relative overflow-hidden">
      <FloatingElement delay="0s" size="h-32 w-32" position="top-10 left-10" />
      <FloatingElement
        delay="2s"
        size="h-24 w-24"
        position="bottom-10 right-20"
      />
      <FloatingElement delay="4s" size="h-16 w-16" position="top-40 right-10" />
      <FloatingElement
        delay="6s"
        size="h-40 w-40"
        position="bottom-20 left-20"
      />

      <Loader loading={loading || false} />
      {renderForm()}

      <style jsx global>{`
        @keyframes slide-in {
          0% {
            opacity: 0;
            transform: translateX(-20px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slide-out {
          0% {
            opacity: 1;
            transform: translateX(0);
          }
          100% {
            opacity: 0;
            transform: translateX(20px);
          }
        }

        @keyframes shake {
          0%,
          100% {
            transform: translateX(0);
          }
          10%,
          30%,
          50%,
          70%,
          90% {
            transform: translateX(-5px);
          }
          20%,
          40%,
          60%,
          80% {
            transform: translateX(5px);
          }
        }

        @keyframes success-bounce {
          0%,
          20%,
          50%,
          80%,
          100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-20px);
          }
          60% {
            transform: translateY(-10px);
          }
        }

        @keyframes fade-in {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pop-in {
          0% {
            transform: scale(0.8);
            opacity: 0;
          }
          70% {
            transform: scale(1.1);
            opacity: 0.7;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes float {
          0% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(5deg);
          }
          100% {
            transform: translateY(0) rotate(0deg);
          }
        }

        .animate-slide-in {
          animation: slide-in 0.4s ease-out forwards;
        }
        .animate-slide-out {
          animation: slide-out 0.4s ease-in forwards;
        }
        .animate-shake {
          animation: shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
        }
        .animate-success-bounce {
          animation: success-bounce 1s;
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
        .animate-pop-in {
          animation: pop-in 0.4s ease-out forwards;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default StoreLogin;
